"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAlertStatusCounts = exports.useSensorAlert = exports.useSensorAlerts = void 0;
const react_1 = require("react");
const mutations_1 = require("../graphql/mutations");
const queries_1 = require("../graphql/queries");
const subscriptions_1 = require("../graphql/subscriptions");
const useLogger_1 = require("./useLogger");
const amplifyBootstrap_1 = require("../utils/amplifyBootstrap");
const sharedAlertPollers = new Map();
const publishSharedAlerts = (sensorId, updater) => {
    const entry = sharedAlertPollers.get(sensorId);
    if (!entry) {
        return;
    }
    entry.alerts = updater(entry.alerts);
    entry.hasData = true;
    entry.listeners.forEach((listener) => listener(entry.alerts));
};
const fetchSharedAlerts = async (sensorId, limit = 50) => {
    try {
        const response = await amplifyBootstrap_1.API.graphql({
            query: queries_1.getSensorAlertsBySensor,
            variables: { sensorId, limit },
        });
        const alerts = (response.data?.getSensorAlertsBySensor || []).map(parseSensorAlert);
        publishSharedAlerts(sensorId, () => alerts);
    }
    catch (error) {
        console.error('Error fetching sensor alerts:', error);
        // matches the previous per-instance behavior: a failed poll clears alerts
        publishSharedAlerts(sensorId, () => []);
    }
};
const createSharedAlertPoller = (sensorId) => {
    const entry = {
        refCount: 0,
        alerts: [],
        hasData: false,
        listeners: new Set(),
        // TODO: this is temporary, remove it when we have proper subscription HB-900 https://treeline.atlassian.net/browse/HB-900
        intervalId: setInterval(() => {
            fetchSharedAlerts(sensorId);
        }, 30000),
        subscriptions: [],
    };
    sharedAlertPollers.set(sensorId, entry);
    fetchSharedAlerts(sensorId);
    try {
        const createSubscription = amplifyBootstrap_1.API.graphql({
            query: subscriptions_1.onCreateSensorAlertBySensor,
            variables: { sensorId },
        }).subscribe({
            next: ({ data }) => {
                const newAlert = data.onSensorSensorAlertCreated;
                if (newAlert) {
                    publishSharedAlerts(sensorId, (prevAlerts) => [parseSensorAlert(newAlert), ...prevAlerts].slice(0, 100));
                }
            },
            error: (error) => console.error('Create sensor alert subscription error:', error),
        });
        const updateSubscription = amplifyBootstrap_1.API.graphql({
            query: subscriptions_1.onUpdateSensorAlertBySensor,
            variables: { sensorId },
        }).subscribe({
            next: ({ data }) => {
                const updatedAlert = data.onSensorSensorAlertUpdated;
                if (updatedAlert) {
                    publishSharedAlerts(sensorId, (prevAlerts) => {
                        const parsed = parseSensorAlert(updatedAlert);
                        const existingIndex = prevAlerts.findIndex((a) => a.id === parsed.id);
                        if (existingIndex === -1) {
                            return [parsed, ...prevAlerts].slice(0, 100);
                        }
                        const clone = [...prevAlerts];
                        clone[existingIndex] = { ...clone[existingIndex], ...parsed };
                        return clone;
                    });
                }
            },
            error: (error) => console.error('Update sensor alert subscription error:', error),
        });
        const serviceRequestSubscription = amplifyBootstrap_1.API.graphql({
            query: subscriptions_1.onUpdateServiceRequest,
        }).subscribe({
            next: ({ data }) => {
                const updatedServiceRequest = data?.onServiceRequestUpdated;
                if (updatedServiceRequest?.id) {
                    publishSharedAlerts(sensorId, (prevAlerts) => prevAlerts.map((alert) => {
                        if (alert.serviceRequest?.id === updatedServiceRequest.id) {
                            return {
                                ...alert,
                                serviceRequest: updatedServiceRequest,
                            };
                        }
                        return alert;
                    }));
                }
            },
            error: (error) => console.error('Service request subscription error:', error),
        });
        entry.subscriptions = [createSubscription, updateSubscription, serviceRequestSubscription];
    }
    catch (err) {
        console.error('Error setting up sensor alerts subscription:', err);
    }
    return entry;
};
const useSensorAlerts = (sensorId) => {
    const [sensorAlerts, setSensorAlerts] = (0, react_1.useState)([]);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const logger = (0, useLogger_1.useLogger)('useSensorAlerts');
    const fetchAndUpdateSensorAlerts = async (sensorId, limit = 50) => {
        if (!sensorId) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            if (sharedAlertPollers.has(sensorId)) {
                // refresh through the shared poller so every attached consumer updates
                await fetchSharedAlerts(sensorId, limit);
            }
            else {
                const sensorAlerts = await fetchSensorAlerts(limit, sensorId);
                setSensorAlerts(sensorAlerts);
            }
        }
        catch (error) {
            logger.error('Error fetching sensor alerts:', { error });
            setError(error);
            setSensorAlerts([]);
        }
        finally {
            setLoading(false);
        }
    };
    const fetchSensorAlerts = async (limit = 50, sensorId) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorAlertsBySensor,
                variables: { sensorId, limit },
            });
            const sensorAlerts = response.data?.getSensorAlertsBySensor || [];
            return sensorAlerts.map(parseSensorAlert);
        }
        catch (error) {
            logger.error('Error fetching sensor alerts:', { error });
            return [];
        }
    };
    const fetchAndUpdateAllSensorAlerts = async (limit = 50, nextToken, filters) => {
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.listSensorAlerts,
                variables: {
                    limit,
                    nextToken,
                    customerId: filters?.customerId,
                    alertType: filters?.alertType,
                    severity: filters?.severity,
                    status: filters?.status,
                },
            });
            const responseData = response.data?.listSensorAlerts;
            const pageAlerts = Array.isArray(responseData) ? responseData : responseData?.items || [];
            const sensorAlerts = pageAlerts.map(parseSensorAlert);
            setSensorAlerts(sensorAlerts);
            const paginationNextToken = Array.isArray(responseData) ? undefined : responseData?.nextToken;
            return {
                alerts: sensorAlerts,
                nextToken: paginationNextToken,
                hasMore: !!paginationNextToken,
            };
        }
        catch (error) {
            logger.error('Error fetching all sensor alerts:', { error });
            setError(error);
            setSensorAlerts([]);
            return {
                alerts: [],
                nextToken: undefined,
                hasMore: false,
            };
        }
        finally {
            setLoading(false);
        }
    };
    const clear = () => {
        setSensorAlerts([]);
    };
    (0, react_1.useEffect)(() => {
        if (!sensorId) {
            return;
        }
        const entry = sharedAlertPollers.get(sensorId) ?? createSharedAlertPoller(sensorId);
        entry.refCount += 1;
        const listener = (alerts) => {
            setSensorAlerts(alerts);
            setLoading(false);
        };
        entry.listeners.add(listener);
        if (entry.hasData) {
            setSensorAlerts(entry.alerts);
        }
        else {
            setLoading(true);
        }
        return () => {
            entry.refCount -= 1;
            entry.listeners.delete(listener);
            if (entry.refCount <= 0) {
                clearInterval(entry.intervalId);
                entry.subscriptions.forEach((subscription) => {
                    if (subscription && typeof subscription.unsubscribe === 'function') {
                        subscription.unsubscribe();
                    }
                });
                sharedAlertPollers.delete(sensorId);
            }
        };
    }, [sensorId]);
    const fetchAllSensorAlertsWithPagination = async (limit = 50, nextToken) => {
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.listSensorAlerts,
                variables: { limit, nextToken },
            });
            const responseData = response.data?.listSensorAlerts;
            const pageAlerts = Array.isArray(responseData) ? responseData : responseData?.items || [];
            const sensorAlerts = pageAlerts.map(parseSensorAlert);
            setSensorAlerts(sensorAlerts);
            const paginationNextToken = Array.isArray(responseData) ? undefined : responseData?.nextToken;
            return {
                alerts: sensorAlerts,
                nextToken: paginationNextToken,
                hasMore: !!paginationNextToken,
            };
        }
        catch (error) {
            logger.error('Error fetching paginated sensor alerts:', { error });
            setError(error);
            setSensorAlerts([]);
            return {
                alerts: [],
                nextToken: undefined,
                hasMore: false,
            };
        }
        finally {
            setLoading(false);
        }
    };
    const memoizedSensorAlerts = (0, react_1.useMemo)(() => sensorAlerts, [sensorAlerts]);
    return {
        sensorAlerts: memoizedSensorAlerts,
        loading,
        error,
        fetchSensorAlerts,
        fetchAndUpdateSensorAlerts,
        fetchAndUpdateAllSensorAlerts,
        fetchAllSensorAlertsWithPagination,
        clear,
    };
};
exports.useSensorAlerts = useSensorAlerts;
const useSensorAlert = (id) => {
    const [sensorAlert, setSensorAlert] = (0, react_1.useState)(null);
    const [isFetching, setIsFetching] = (0, react_1.useState)(false);
    const [isUpdating, setIsUpdating] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const fetchSensorAlert = async () => {
        try {
            setIsFetching(true);
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorAlert,
                variables: { id },
            });
            if (response && response.data && response.data.getSensorAlert) {
                setSensorAlert(parseSensorAlert(response.data.getSensorAlert));
            }
            else {
                console.warn('Unexpected API response structure:', response);
                setSensorAlert(null);
            }
            setError(null);
        }
        catch (error) {
            setError(error);
            console.error('Error fetching sensor alert:', error);
        }
        finally {
            setIsFetching(false);
        }
    };
    const updateAlertStatus = async (input) => {
        try {
            setIsUpdating(true);
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateSensorAlertStatus,
                variables: { input },
            });
            const updatedAlert = response.data.updateSensorAlertStatus;
            const parsedSensorAlert = parseSensorAlert(updatedAlert);
            setSensorAlert({ ...(sensorAlert || {}), ...parsedSensorAlert });
            return parsedSensorAlert;
        }
        catch (err) {
            console.error('Error updating sensor alert status:', err);
            throw err;
        }
        finally {
            setIsUpdating(false);
        }
    };
    const updateAlertAcknowledgedState = async (input) => {
        try {
            setIsUpdating(true);
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateSensorAlertAcknowledgedState,
                variables: { input },
            });
            const updatedAlert = response.data.updateSensorAlertAcknowledgedState;
            const parsedSensorAlert = parseSensorAlert(updatedAlert);
            setSensorAlert({ ...(sensorAlert || {}), ...parsedSensorAlert });
            return parsedSensorAlert;
        }
        catch (err) {
            console.error('Error updating acknowledged state for sensor alert:', err);
            throw err;
        }
        finally {
            setIsUpdating(false);
        }
    };
    (0, react_1.useEffect)(() => {
        if (id) {
            fetchSensorAlert();
        }
    }, [id]);
    return {
        sensorAlert,
        isFetching,
        error,
        isUpdating,
        updateSensorAlertStatus: updateAlertStatus,
        updateSensorAlertAcknowledgedState: updateAlertAcknowledgedState,
    };
};
exports.useSensorAlert = useSensorAlert;
const useAlertStatusCounts = (filters) => {
    const [counts, setCounts] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const fetchCounts = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorAlertStatusCounts,
                variables: {
                    customerId: filters?.customerId,
                    alertType: filters?.alertType,
                    severity: filters?.severity,
                },
            });
            if (response?.data?.getSensorAlertStatusCounts) {
                setCounts(response.data.getSensorAlertStatusCounts);
            }
        }
        catch (error) {
            setError(error);
            console.error('Error fetching alert status counts:', error);
        }
        finally {
            setLoading(false);
        }
    };
    (0, react_1.useEffect)(() => {
        fetchCounts();
    }, [filters?.customerId, filters?.alertType, filters?.severity]);
    return {
        counts,
        loading,
        error,
        refetch: fetchCounts,
    };
};
exports.useAlertStatusCounts = useAlertStatusCounts;
const parseSensorAlert = (alert) => {
    let parsedMetadata = {};
    let parsedValue = alert.value;
    if (typeof alert.metadata === 'string') {
        try {
            parsedMetadata = JSON.parse(alert.metadata);
        }
        catch {
            parsedMetadata = { raw: alert.metadata };
        }
    }
    else if (typeof alert.metadata === 'object' && alert.metadata !== null) {
        parsedMetadata = alert.metadata;
    }
    if (typeof alert.value === 'string') {
        try {
            parsedValue = JSON.parse(alert.value);
        }
        catch {
            parsedValue = alert.value;
        }
    }
    // Safely handle parsedValue which might be null/undefined
    const powerMonitorInputNumber = parsedValue && parsedValue.branch && typeof parsedValue.branch === 'number'
        ? parsedValue.branch
        : parsedValue && typeof parsedValue.branch === 'string' && !isNaN(Number(parsedValue.branch))
            ? Number(parsedValue.branch)
            : undefined;
    return {
        ...alert,
        value: parsedValue?.value || parsedValue,
        parsedMetadata,
        powerMonitorInputNumber,
    };
};
